import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { adminMatchesAPI } from '../services/adminApi';
import styled from 'styled-components';
import { getLocalStorageItem } from '../utils/safeStorage';
import AdminScoreModal from './AdminScoreModal';
import { MatchPadelGamesDetail } from './MatchPadelGamesDetail';
import { consumeAdminNavHandoff } from '../utils/adminNavFilters';
import { useAdminSeasonPhaseFilter } from '../hooks/useAdminSeasonPhaseFilter';
import AdminSeasonPhaseFilters from './AdminSeasonPhaseFilters';

const MatchesContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const MatchesHeader = styled.div`
  background: linear-gradient(135deg, #043663 0%, #1e40af 100%);
  color: white;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const FilterTab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.active ? '#043663' : 'white'};
  
  &:hover {
    background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const SearchContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  min-width: 150px;
  
  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }
`;

const AccordionContainer = styled.div`
  max-height: 600px;
  overflow-y: auto;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e5e7eb;
  
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  cursor: pointer;
  background: ${props => props.isOpen ? '#f8fafc' : 'white'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f8fafc;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const HeaderTitle = styled.span`
  font-weight: 600;
  color: #1f2937;
  font-size: 1rem;
`;

const CountBadge = styled.span`
  background: #043663;
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
`;

const ChevronIcon = styled.span`
  font-size: 1.2rem;
  color: #6b7280;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
`;

const AccordionContent = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
`;

const SubAccordionItem = styled.div`
  background: white;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SubAccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  cursor: pointer;
  background: ${props => props.isOpen ? '#f1f5f9' : 'white'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #f1f5f9;
  }
`;

const SubHeaderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SubHeaderTitle = styled.span`
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const SubCountBadge = styled.span`
  background: #6b7280;
  color: white;
  padding: 0.2rem 0.4rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
`;

const SubAccordionContent = styled.div`
  padding: 0 1rem 1rem 1rem;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
`;

const MatchItem = styled.div`
  background: white;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border: 1px solid #e5e7eb;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const MatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

const MatchTeams = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.85rem;
`;

const MatchStatus = styled.span`
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'completed':
        return 'background: #dcfce7; color: #166534;';
      case 'in_progress':
        return 'background: #fef3c7; color: #92400e;';
      case 'scheduled':
        return 'background: #dbeafe; color: #1e40af;';
      case 'cancelled':
        return 'background: #fee2e2; color: #dc2626;';
      default:
        return 'background: #f3f4f6; color: #6b7280;';
    }
  }}
`;

const MatchDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6b7280;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;

const DetailLabel = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 0.7rem;
  text-transform: uppercase;
`;

const DetailValue = styled.span`
  color: #1f2937;
  font-size: 0.8rem;
`;

const LoadingContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 2rem;
  text-align: center;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 8px;
  margin: 1rem;
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.variant === 'danger' ? '#dc2626' : '#043663'};
  color: white;
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'danger' ? '#b91c1c' : '#032a4f'};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: flex-end;
`;

const ClickableMatchItem = styled(MatchItem)`
  cursor: pointer;
  &:hover {
    background: #f0f9ff;
    border-color: #043663;
  }
`;

const AdminMatchesNew = () => {
  const [activeFilter, setActiveFilter] = useState('canton'); // 'canton' or 'date'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [openCantons, setOpenCantons] = useState(new Set());
  const [openDates, setOpenDates] = useState(new Set());
  const [openTeams, setOpenTeams] = useState(new Set());
  const [clearingMatches, setClearingMatches] = useState(false);
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  const queryClient = useQueryClient();

  const [preferredPhaseId, setPreferredPhaseId] = useState('');

  useEffect(() => {
    const h = consumeAdminNavHandoff();
    if (!h) return;
    if (h.section && h.section !== 'matches') return;
    if (h.seasonId != null && h.seasonId !== '') setPreferredPhaseId(String(h.seasonId));
    if (h.matchStatus != null) setStatusFilter(String(h.matchStatus));
  }, []);

  const seasonPhase = useAdminSeasonPhaseFilter({
    allowAll: true,
    autoDefault: true,
    preferredPhaseId,
  });
  const selectedSeason = seasonPhase.seasonId;

  // Fetch matches data
  const { data: matchesData, isLoading, error } = useQuery(
    ['adminMatches', statusFilter, selectedSeason],
    () => adminMatchesAPI.getAll({ 
      status: statusFilter || undefined,
      season_id: selectedSeason || undefined 
    }),
    {
      staleTime: 30000,
      cacheTime: 300000,
    }
  );

  const toggleCanton = (canton) => {
    const newOpenCantons = new Set(openCantons);
    if (newOpenCantons.has(canton)) {
      newOpenCantons.delete(canton);
    } else {
      newOpenCantons.add(canton);
    }
    setOpenCantons(newOpenCantons);
  };

  const toggleDate = (date) => {
    const newOpenDates = new Set(openDates);
    if (newOpenDates.has(date)) {
      newOpenDates.delete(date);
    } else {
      newOpenDates.add(date);
    }
    setOpenDates(newOpenDates);
  };

  const toggleTeam = (teamKey) => {
    const newOpenTeams = new Set(openTeams);
    if (newOpenTeams.has(teamKey)) {
      newOpenTeams.delete(teamKey);
    } else {
      newOpenTeams.add(teamKey);
    }
    setOpenTeams(newOpenTeams);
  };

  const handleClearAllMatches = async () => {
    if (!window.confirm('⚠️ WARNING: This will clear ALL matches and reset ALL team standings (points, wins, losses, set differences). This action cannot be undone. Are you sure?')) {
      return;
    }

    if (!window.confirm('Are you absolutely certain? This will delete all match data and reset all team statistics.')) {
      return;
    }

    setClearingMatches(true);
    try {
      // First, get the current active season ID
      const seasonResponse = await fetch('/api/admin/seasons', {
        headers: {
          'Authorization': `Bearer ${getLocalStorageItem('adminToken')}`
        }
      });
      
      if (!seasonResponse.ok) {
        throw new Error('Failed to get phases');
      }
      
      const seasonData = await seasonResponse.json();
      const activeSeason = seasonData.data?.find(season => season.status === 'active');
      
      if (!activeSeason) {
        throw new Error('No active phase found. Please create an active phase first.');
      }
      
      console.log('Using season ID:', activeSeason.id);

      // Call the reset scoring endpoint which clears matches AND team standings
      const response = await fetch('/api/scores/admin/reset-scoring', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getLocalStorageItem('adminToken')}`
        },
        body: JSON.stringify({
          seasonId: activeSeason.id // Use the actual season ID
        })
      });

      const result = await response.json();

      if (result.success) {
        alert('✅ All matches cleared and team standings reset successfully!');
        // Refresh the matches data
        queryClient.invalidateQueries(['adminMatches']);
        queryClient.invalidateQueries(['adminOverview']);
        queryClient.invalidateQueries(['dashboard']);
      } else {
        alert(`❌ Error clearing matches: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error clearing matches:', error);
      alert(`❌ Error clearing matches: ${error.message}`);
    } finally {
      setClearingMatches(false);
    }
  };

  if (isLoading) {
    return (
      <MatchesContainer>
        <LoadingContainer>Loading matches...</LoadingContainer>
      </MatchesContainer>
    );
  }

  if (error) {
    return (
      <MatchesContainer>
        <ErrorContainer>Error loading matches: {error.message}</ErrorContainer>
      </MatchesContainer>
    );
  }

  const matches = matchesData?.data?.matches || [];

  // Group matches by canton
  const matchesByCanton = {};
  matches.forEach(match => {
    const canton = match.canton;
    if (!matchesByCanton[canton]) {
      matchesByCanton[canton] = {};
    }
    
    const team1Name = match.team1_name;
    const team2Name = match.team2_name;
    
    if (!matchesByCanton[canton][team1Name]) {
      matchesByCanton[canton][team1Name] = [];
    }
    if (!matchesByCanton[canton][team2Name]) {
      matchesByCanton[canton][team2Name] = [];
    }
    
    matchesByCanton[canton][team1Name].push({ ...match, isTeam1: true });
    matchesByCanton[canton][team2Name].push({ ...match, isTeam1: false });
  });

  // Group matches by date
  const matchesByDate = {};
  matches.forEach(match => {
    if (match.game_window_start) {
      const date = new Date(match.game_window_start).toDateString();
      if (!matchesByDate[date]) {
        matchesByDate[date] = {};
      }
      
      const team1Name = match.team1_name;
      const team2Name = match.team2_name;
      
      if (!matchesByDate[date][team1Name]) {
        matchesByDate[date][team1Name] = [];
      }
      if (!matchesByDate[date][team2Name]) {
        matchesByDate[date][team2Name] = [];
      }
      
      matchesByDate[date][team1Name].push({ ...match, isTeam1: true });
      matchesByDate[date][team2Name].push({ ...match, isTeam1: false });
    }
  });

  // Filter data based on search term
  const filterData = (data) => {
    if (!searchTerm) return data;
    
    const filtered = {};
    Object.keys(data).forEach(key => {
      const teams = Object.keys(data[key]).filter(teamName =>
        teamName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (teams.length > 0) {
        filtered[key] = {};
        teams.forEach(team => {
          filtered[key][team] = data[key][team];
        });
      }
    });
    return filtered;
  };

  const filteredMatchesByCanton = filterData(matchesByCanton);
  const filteredMatchesByDate = filterData(matchesByDate);

  return (
    <MatchesContainer>
      <MatchesHeader>
        <h3 style={{ margin: '0 0 1rem 0', fontSize: '1.5rem', fontWeight: '700' }}>
          Matches Management
        </h3>
        <p style={{ margin: '0 0 1rem 0', opacity: 0.9 }}>
          View and manage all matches by canton or date
        </p>
        
        <FilterTabs>
          <FilterTab 
            active={activeFilter === 'canton'} 
            onClick={() => setActiveFilter('canton')}
          >
            By Canton
          </FilterTab>
          <FilterTab 
            active={activeFilter === 'date'} 
            onClick={() => setActiveFilter('date')}
          >
            By Date
          </FilterTab>
        </FilterTabs>
        
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search teams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <AdminSeasonPhaseFilters
            campaignId={seasonPhase.campaignId}
            seasonId={seasonPhase.seasonId}
            seasonOptions={seasonPhase.seasonOptions}
            phaseOptions={seasonPhase.phaseOptions}
            onCampaignChange={seasonPhase.onCampaignChange}
            onPhaseChange={seasonPhase.onPhaseChange}
            allowAll
            disabled={seasonPhase.isLoading}
          />
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="score_submitted">Pending Confirmation</option>
            <option value="completed">Completed</option>
            <option value="disputed">Disputed</option>
            <option value="cancelled">Cancelled</option>
          </FilterSelect>
        </SearchContainer>
        
        <ActionButtonsContainer style={{ display: 'none' }}>
          <ActionButton 
            variant="danger" 
            onClick={handleClearAllMatches}
            disabled={clearingMatches}
          >
            {clearingMatches ? 'Clearing...' : '🗑️ Clear All Matches & Reset Standings'}
          </ActionButton>
        </ActionButtonsContainer>
      </MatchesHeader>

      <AccordionContainer>
        {activeFilter === 'canton' ? (
          // By Canton View
          Object.keys(filteredMatchesByCanton).length > 0 ? (
            Object.keys(filteredMatchesByCanton).sort().map(canton => {
              const cantonTeams = filteredMatchesByCanton[canton];
              const totalTeams = Object.keys(cantonTeams).length;
              const isOpen = openCantons.has(canton);

              return (
                <AccordionItem key={canton}>
                  <AccordionHeader
                    isOpen={isOpen}
                    onClick={() => toggleCanton(canton)}
                  >
                    <HeaderInfo>
                      <HeaderTitle>{canton}</HeaderTitle>
                      <CountBadge>{totalTeams} teams</CountBadge>
                    </HeaderInfo>
                    <ChevronIcon isOpen={isOpen}>▼</ChevronIcon>
                  </AccordionHeader>

                  {isOpen && (
                    <AccordionContent>
                      {Object.keys(cantonTeams).sort().map(teamName => {
                        const teamMatches = cantonTeams[teamName];
                        const teamKey = `${canton}-${teamName}`;
                        const isTeamOpen = openTeams.has(teamKey);

                        return (
                          <SubAccordionItem key={teamName}>
                            <SubAccordionHeader
                              isOpen={isTeamOpen}
                              onClick={() => toggleTeam(teamKey)}
                            >
                              <SubHeaderInfo>
                                <SubHeaderTitle>{teamName}</SubHeaderTitle>
                                <SubCountBadge>{teamMatches.length} matches</SubCountBadge>
                              </SubHeaderInfo>
                              <ChevronIcon isOpen={isTeamOpen}>▼</ChevronIcon>
                            </SubAccordionHeader>

                            {isTeamOpen && (
                              <SubAccordionContent>
                                {teamMatches.map((match, index) => (
                                  <ClickableMatchItem
                                    key={`${match.id}-${index}`}
                                    onClick={() => setScoreModalMatch(match)}
                                  >
                                    <MatchHeader>
                                      <MatchTeams>
                                        {match.team1_name} vs {match.team2_name}
                                      </MatchTeams>
                                      <MatchStatus status={match.status}>
                                        {match.status}
                                      </MatchStatus>
                                    </MatchHeader>

                                    <MatchDetails>
                                      <DetailItem>
                                        <DetailLabel>Date & Time</DetailLabel>
                                        <DetailValue>
                                          {match.game_window_start ? 
                                            new Date(match.game_window_start).toLocaleString() : 
                                            'TBD'
                                          }
                                        </DetailValue>
                                      </DetailItem>

                                      <DetailItem>
                                        <DetailLabel>Venue</DetailLabel>
                                        <DetailValue>
                                          {match.court_name || match.venue || 'TBD'}
                                        </DetailValue>
                                      </DetailItem>

                                      {(match.match_details || match.match_date || match.match_time) && (
                                        <DetailItem>
                                          <DetailLabel>Match Details</DetailLabel>
                                          <DetailValue>
                                            {match.match_details || [match.match_date, match.match_time].filter(Boolean).join(' ')}
                                          </DetailValue>
                                        </DetailItem>
                                      )}

                                      <DetailItem>
                                        <DetailLabel>Division</DetailLabel>
                                        <DetailValue>
                                          {match.division_level} {match.group_name}
                                        </DetailValue>
                                      </DetailItem>

                                      {match.status === 'completed' && (
                                        <DetailItem>
                                          <DetailLabel>Score</DetailLabel>
                                          <DetailValue>
                                            {match.team1_score_set1 !== null ? 
                                              `${match.team1_score_set1}-${match.team2_score_set1}, ${match.team1_score_set2}-${match.team2_score_set2}${(match.team1_score_set3 !== null && match.team1_score_set3 !== undefined && match.team2_score_set3 !== null && match.team2_score_set3 !== undefined && (match.team1_score_set3 > 0 || match.team2_score_set3 > 0)) ? `, ${match.team1_score_set3}-${match.team2_score_set3}` : ''}` :
                                              'No score recorded'
                                            }
                                          </DetailValue>
                                        </DetailItem>
                                      )}
                                      <MatchPadelGamesDetail
                                        match={match}
                                        DetailItem={DetailItem}
                                        DetailLabel={DetailLabel}
                                        DetailValue={DetailValue}
                                      />
                                    </MatchDetails>
                                  </ClickableMatchItem>
                                ))}
                              </SubAccordionContent>
                            )}
                          </SubAccordionItem>
                        );
                      })}
                    </AccordionContent>
                  )}
                </AccordionItem>
              );
            })
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              {searchTerm ? 'No teams found matching your search.' : 'No matches found.'}
            </div>
          )
        ) : (
          // By Date View
          Object.keys(filteredMatchesByDate).length > 0 ? (
            Object.keys(filteredMatchesByDate).sort((a, b) => new Date(a) - new Date(b)).map(date => {
              const dateTeams = filteredMatchesByDate[date];
              const totalTeams = Object.keys(dateTeams).length;
              const isOpen = openDates.has(date);

              return (
                <AccordionItem key={date}>
                  <AccordionHeader
                    isOpen={isOpen}
                    onClick={() => toggleDate(date)}
                  >
                    <HeaderInfo>
                      <HeaderTitle>{date}</HeaderTitle>
                      <CountBadge>{totalTeams} teams</CountBadge>
                    </HeaderInfo>
                    <ChevronIcon isOpen={isOpen}>▼</ChevronIcon>
                  </AccordionHeader>

                  {isOpen && (
                    <AccordionContent>
                      {Object.keys(dateTeams).sort().map(teamName => {
                        const teamMatches = dateTeams[teamName];
                        const teamKey = `${date}-${teamName}`;
                        const isTeamOpen = openTeams.has(teamKey);

                        return (
                          <SubAccordionItem key={teamName}>
                            <SubAccordionHeader
                              isOpen={isTeamOpen}
                              onClick={() => toggleTeam(teamKey)}
                            >
                              <SubHeaderInfo>
                                <SubHeaderTitle>{teamName}</SubHeaderTitle>
                                <SubCountBadge>{teamMatches.length} matches</SubCountBadge>
                              </SubHeaderInfo>
                              <ChevronIcon isOpen={isTeamOpen}>▼</ChevronIcon>
                            </SubAccordionHeader>

                            {isTeamOpen && (
                              <SubAccordionContent>
                                {teamMatches.map((match, index) => (
                                  <ClickableMatchItem
                                    key={`${match.id}-${index}`}
                                    onClick={() => setScoreModalMatch(match)}
                                  >
                                    <MatchHeader>
                                      <MatchTeams>
                                        {match.team1_name} vs {match.team2_name}
                                      </MatchTeams>
                                      <MatchStatus status={match.status}>
                                        {match.status}
                                      </MatchStatus>
                                    </MatchHeader>

                                    <MatchDetails>
                                      <DetailItem>
                                        <DetailLabel>Time</DetailLabel>
                                        <DetailValue>
                                          {match.game_window_start ? 
                                            new Date(match.game_window_start).toLocaleTimeString() : 
                                            'TBD'
                                          }
                                        </DetailValue>
                                      </DetailItem>

                                      <DetailItem>
                                        <DetailLabel>Venue</DetailLabel>
                                        <DetailValue>
                                          {match.court_name || match.venue || 'TBD'}
                                        </DetailValue>
                                      </DetailItem>

                                      {(match.match_details || match.match_date || match.match_time) && (
                                        <DetailItem>
                                          <DetailLabel>Match Details</DetailLabel>
                                          <DetailValue>
                                            {match.match_details || [match.match_date, match.match_time].filter(Boolean).join(' ')}
                                          </DetailValue>
                                        </DetailItem>
                                      )}

                                      <DetailItem>
                                        <DetailLabel>Division</DetailLabel>
                                        <DetailValue>
                                          {match.canton} - {match.division_level} {match.group_name}
                                        </DetailValue>
                                      </DetailItem>

                                      {match.status === 'completed' && (
                                        <DetailItem>
                                          <DetailLabel>Score</DetailLabel>
                                          <DetailValue>
                                            {match.team1_score_set1 !== null ? 
                                              `${match.team1_score_set1}-${match.team2_score_set1}, ${match.team1_score_set2}-${match.team2_score_set2}${(match.team1_score_set3 !== null && match.team1_score_set3 !== undefined && match.team2_score_set3 !== null && match.team2_score_set3 !== undefined && (match.team1_score_set3 > 0 || match.team2_score_set3 > 0)) ? `, ${match.team1_score_set3}-${match.team2_score_set3}` : ''}` :
                                              'No score recorded'
                                            }
                                          </DetailValue>
                                        </DetailItem>
                                      )}
                                      <MatchPadelGamesDetail
                                        match={match}
                                        DetailItem={DetailItem}
                                        DetailLabel={DetailLabel}
                                        DetailValue={DetailValue}
                                      />
                                    </MatchDetails>
                                  </ClickableMatchItem>
                                ))}
                              </SubAccordionContent>
                            )}
                          </SubAccordionItem>
                        );
                      })}
                    </AccordionContent>
                  )}
                </AccordionItem>
              );
            })
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
              {searchTerm ? 'No teams found matching your search.' : 'No matches found.'}
            </div>
          )
        )}
      </AccordionContainer>

      <AdminScoreModal
        isOpen={!!scoreModalMatch}
        onClose={() => setScoreModalMatch(null)}
        match={scoreModalMatch}
        onSuccess={() => queryClient.invalidateQueries(['adminMatches'])}
      />
    </MatchesContainer>
  );
};

export default AdminMatchesNew;
